// Templates in the composer and the New text dialog: pick one, it is rendered with the company
// and the contact's name, and any token that is still empty (time, tech...) is asked for in a
// tiny form. The result is plain text the person can still edit before sending.

import { useMemo, useState } from 'react';
import { renderTemplate, unresolvedTokens } from '@bt/core';
import type { Template, TemplateCategory } from '../api';

export const CATEGORY_LABEL: Record<TemplateCategory, string> = {
  first_message: 'First message',
  reminder: 'Reminder',
  other: 'Other',
};

/** the order templates are grouped in the menus */
export const CATEGORY_ORDER: TemplateCategory[] = ['first_message', 'other', 'reminder'];

export function categoryOf(t: Template): TemplateCategory {
  return t.category ?? 'other';
}

const TOKEN_LABELS: Record<string, { label: string; placeholder: string }> = {
  time: { label: 'Time', placeholder: 'e.g. 2:30 PM' },
  tech: { label: "Technician's name", placeholder: 'e.g. Mike' },
  date: { label: 'Date', placeholder: 'e.g. Thursday, Sep 4' },
  name: { label: "Customer's name", placeholder: 'e.g. Maria' },
  company: { label: 'Company name', placeholder: '' },
};

export function tokenLabel(key: string): { label: string; placeholder: string } {
  const k = key.trim().toLowerCase();
  return TOKEN_LABELS[k] ?? { label: k.replace(/_/g, ' '), placeholder: '' };
}

/** One of the wordings at random, so repeated first texts do not all read the same (trap 9). */
export function pickWording(t: Template): string {
  const v = t.variants.filter((x) => x.trim());
  if (v.length === 0) return '';
  return v[Math.floor(Math.random() * v.length)];
}

/** Inputs for the tokens a template still needs. Small on purpose; it sits inside a popover. */
export function TokenForm({ tokens, values, onChange, autoFocus }: { tokens: string[]; values: Record<string, string>; onChange: (key: string, value: string) => void; autoFocus?: boolean }) {
  return (
    <div className="token-form">
      {tokens.map((k, i) => {
        const meta = tokenLabel(k);
        return (
          <label key={k} className="field" style={{ marginBottom: 8 }}>
            <span>{meta.label}</span>
            <input className="input" value={values[k] ?? ''} placeholder={meta.placeholder} onChange={(e) => onChange(k, e.target.value)} autoFocus={autoFocus && i === 0} />
          </label>
        );
      })}
    </div>
  );
}

/**
 * The composer's Template menu. `vars` = what we already know (company, name). Calls onFill with
 * the rendered text once every token has a value.
 */
export function TemplateMenu({ templates, vars, onFill, onClose, loading }: { templates: Template[]; vars: Record<string, string>; onFill: (text: string, template: Template) => void; onClose: () => void; loading?: boolean }) {
  const [chosen, setChosen] = useState<{ template: Template; wording: string } | null>(null);
  const [extra, setExtra] = useState<Record<string, string>>({});

  const groups = useMemo(() => {
    const byCat = new Map<TemplateCategory, Template[]>();
    for (const t of templates) {
      const c = categoryOf(t);
      if (!byCat.has(c)) byCat.set(c, []);
      byCat.get(c)!.push(t);
    }
    return CATEGORY_ORDER.filter((c) => byCat.has(c)).map((c) => ({ category: c, items: byCat.get(c)! }));
  }, [templates]);

  const choose = (t: Template) => {
    const wording = pickWording(t);
    const missing = unresolvedTokens(wording, vars);
    if (missing.length === 0) {
      onFill(renderTemplate(wording, vars), t);
      return;
    }
    setChosen({ template: t, wording });
    setExtra({});
  };

  if (chosen) {
    const missing = unresolvedTokens(chosen.wording, vars);
    const allVars = { ...vars, ...extra };
    const stillMissing = unresolvedTokens(chosen.wording, allVars);
    const preview = renderTemplate(chosen.wording, allVars);
    return (
      <div className="popover template-menu" onMouseDown={(e) => e.stopPropagation()}>
        <div className="row between" style={{ marginBottom: 6 }}>
          <strong className="small">{chosen.template.name}</strong>
          <button className="btn link small" onClick={() => setChosen(null)}>
            Back
          </button>
        </div>
        <div className="tiny muted" style={{ marginBottom: 6 }}>Fill in what the text still needs.</div>
        <TokenForm tokens={missing} values={extra} onChange={(k, v) => setExtra((x) => ({ ...x, [k]: v }))} autoFocus />
        <div className="preview-box small">{preview}</div>
        <div className="row" style={{ justifyContent: 'flex-end', marginTop: 8 }}>
          <button className="btn small" onClick={onClose}>
            Cancel
          </button>
          <button className="btn small primary" disabled={stillMissing.length > 0} onClick={() => onFill(preview, chosen.template)}>
            Use this
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="popover template-menu" onMouseDown={(e) => e.stopPropagation()}>
      {loading && templates.length === 0 && <div className="muted small" style={{ padding: 6 }}>Loading</div>}
      {!loading && templates.length === 0 && <div className="muted small" style={{ padding: 6 }}>No templates yet. Make one under Scheduled › Templates.</div>}
      {groups.map((g) => (
        <div key={g.category}>
          <div className="menu-head">{CATEGORY_LABEL[g.category]}</div>
          {g.items.map((t) => (
            <button key={t.id} type="button" className="menu-item" onClick={() => choose(t)}>
              <span>{t.name}</span>
              {t.variants.length < 3 && <span className="chip warn">{t.variants.length} {t.variants.length === 1 ? 'wording' : 'wordings'}</span>}
            </button>
          ))}
        </div>
      ))}
    </div>
  );
}
